import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Filter, Fuel, AlertTriangle, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Row {
  id: string;
  code: string;
  tripDate: string;
  vehicleId: string | null;
  vehiclePlate: string | null;
  driverId: string | null;
  driverName: string | null;
  kmAllocated: number | null;
  expectedLiters: number | null;
  actualLiters: number | null;
  variancePct: number | null;
  status: 'normal' | 'above_expected' | 'high_waste' | 'investigate' | 'suspicious_low' | null;
  wasteCostSar: number;
}

interface Summary {
  totalTrips: number;
  analyzedTrips: number;
  totalWasteSar: number;
  avgVariancePct: number | null;
  flaggedCount: number;
}

interface LeaderRow { id: string; name: string | null; trips: number; totalWasteSar: number; avgVariancePct: number | null }

const STATUS_FILTERS = [
  { value: '',                 label: 'All' },
  { value: 'investigate',      label: 'Investigate' },
  { value: 'high_waste',       label: 'High waste' },
  { value: 'above_expected',   label: 'Above expected' },
  { value: 'normal',           label: 'Normal' },
  { value: 'suspicious_low',   label: 'Suspicious low' },
  { value: 'unanalyzed',       label: 'Unanalyzed' },
] as const;

const STATUS_TONE: Record<string, string> = {
  normal:           'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  above_expected:   'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  high_waste:       'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30',
  investigate:      'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30',
  suspicious_low:   'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30',
};

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtL   = (n: number | null) => n === null ? '—' : n.toFixed(1) + ' L';
const fmtPct = (n: number | null) => n === null ? '—' : (n > 0 ? '+' : '') + n.toFixed(1) + '%';
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function defaultRange() {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const from = new Date(now.getTime() - 30 * 86400000).toISOString().slice(0, 10);
  return { from, to };
}

export default function ConsumptionPage() {
  const def = useMemo(defaultRange, []);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [status, setStatus] = useState<string>('');

  const [rows, setRows] = useState<Row[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [drivers, setDrivers] = useState<LeaderRow[]>([]);
  const [vehicles, setVehicles] = useState<LeaderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', from);
      params.set('to', to);
      params.set('pageSize', '200');
      if (status) params.set('status', status);

      const [r1, r2, r3, r4] = await Promise.all([
        apiClient.get<Row[]>('/api/v1/consumption/trips?' + params.toString()),
        apiClient.get<Summary>('/api/v1/consumption/summary?from=' + from + '&to=' + to),
        apiClient.get<LeaderRow[]>('/api/v1/consumption/leaderboard?period=last30&by=driver&limit=5'),
        apiClient.get<LeaderRow[]>('/api/v1/consumption/leaderboard?period=last30&by=vehicle&limit=5'),
      ]);
      setRows(r1.data);
      setSummary(r2.data);
      setDrivers(r3.data);
      setVehicles(r4.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [from, to, status]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2">
            <Fuel className="h-4 w-4" /> Consumption analysis
          </div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* Filters */}
        <div className="rounded-lg border border-border p-3 flex flex-wrap items-end gap-3">
          <Filter className="h-4 w-4 text-muted-foreground mt-2" />
          <div className="space-y-1">
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="flex h-11 w-44 rounded-md border border-input bg-background px-3 py-2 text-base"
            >
              {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Total trips"    value={String(summary.totalTrips)} />
            <SummaryCard label="Analyzed"       value={summary.analyzedTrips + ' / ' + summary.totalTrips} />
            <SummaryCard label="Flagged"        value={String(summary.flaggedCount)} tone={summary.flaggedCount > 0 ? 'bad' : undefined} />
            <SummaryCard label="Total waste"    value={fmtSar(summary.totalWasteSar)} tone={summary.totalWasteSar > 0 ? 'bad' : undefined} />
          </div>
        )}

        {/* Leaderboards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Leaderboard title="Worst drivers (last 30d)" rows={drivers} icon={<TrendingDown className="h-4 w-4 text-rose-500" />} />
          <Leaderboard title="Worst vehicles (last 30d)" rows={vehicles} icon={<TrendingDown className="h-4 w-4 text-rose-500" />} />
        </div>

        {/* Table */}
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
            <span>Trips</span>
            <span>{rows.length} rows</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-4 text-sm text-rose-600 dark:text-rose-400 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No trips match.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-background border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-left px-3 py-2">Driver</th>
                    <th className="text-right px-3 py-2">km</th>
                    <th className="text-right px-3 py-2">Expected</th>
                    <th className="text-right px-3 py-2">Actual</th>
                    <th className="text-right px-3 py-2">Variance</th>
                    <th className="text-right px-3 py-2">Waste</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const tone = r.status ? STATUS_TONE[r.status] : '';
                    return (
                      <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/30">
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.tripDate)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.vehiclePlate ?? '—'}</td>
                        <td className="px-3 py-2 truncate max-w-[150px]">{r.driverName ?? '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.kmAllocated?.toFixed(0) ?? '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtL(r.expectedLiters)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtL(r.actualLiters)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtPct(r.variancePct)}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.wasteCostSar > 0 ? fmtSar(r.wasteCostSar) : '—'}</td>
                        <td className="px-3 py-2">
                          {r.status ? (
                            <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + tone}>
                              {r.status.replace('_', ' ')}
                            </span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground italic">unanalyzed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  const cls = tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : '';
  return (
    <div className="bg-muted rounded-lg p-4">
      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{label}</div>
      <div className={'text-2xl font-medium ' + cls}>{value}</div>
    </div>
  );
}

function Leaderboard({ title, rows, icon }: { title: string; rows: LeaderRow[]; icon?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border">
      <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2 w-full">
        {icon} {title}
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs text-muted-foreground">No data.</div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r, i) => (
            <div key={r.id} className="px-4 py-2 flex items-center justify-between text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs text-muted-foreground tabular-nums w-5">{i + 1}.</span>
                <span className="truncate">{r.name ?? '—'}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-muted-foreground">{r.trips} trips</span>
                <span className="font-mono">{fmtSar(r.totalWasteSar)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
